import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { db } from '../firebase';
import { collection, getDocs, limit, orderBy, query } from 'firebase/firestore';
import {
  Alert,
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Link,
  Stack,
  SxProps,
  TextField,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

interface NewsItem {
  id: string;
  title: string;
  link: string;
  source: string;
  description: string;
  imageUrl: string;
  publishedAt: string;
}

// publishedAt in the `articles` collection is a Firestore Timestamp.
const toIso = (value: unknown): string => {
  if (!value) return '';
  if (typeof (value as { toDate?: () => Date }).toDate === 'function') {
    return (value as { toDate: () => Date }).toDate().toISOString();
  }
  return String(value);
};

// Relative time for recent articles ("3h ago"), absolute date once that
// stops being useful — agents prune anything older than 60 days anyway.
const formatPublished = (iso: string): string => {
  const d = dayjs(iso);
  if (!d.isValid()) return '';
  return d.isAfter(dayjs().subtract(14, 'day')) ? d.fromNow() : d.format('MMM D, YYYY');
};

// Newspaper-style serif for headlines; the rest of the app stays sans-serif.
const HEADLINE_FONT = '"Georgia", "Times New Roman", Times, serif';

const ImagePlaceholder: React.FC<{ sx?: SxProps<Theme> }> = ({ sx }) => (
  <Box
    sx={[
      {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
      },
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}
  >
    <NewspaperIcon sx={{ fontSize: 40, color: 'text.secondary', opacity: 0.6 }} />
  </Box>
);

const News: React.FC = () => {
  const [items, setItems] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sourceFilter, setSourceFilter] = useState<string>('All');
  const [search, setSearch] = useState('');

  const fetchNews = useCallback(async () => {
    setError('');
    try {
      const snapshot = await getDocs(
        query(collection(db, 'articles'), orderBy('publishedAt', 'desc'), limit(100))
      );
      // Map the agent-posted article schema onto the card fields.
      setItems(
        snapshot.docs.map(docSnap => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            title: data.title || '',
            link: data.sourceUrl || data.link || '',
            source: data.source || '',
            description: data.summary || data.description || '',
            imageUrl: data.imageUrl || '',
            publishedAt: toIso(data.publishedAt),
          };
        })
      );
    } catch (err) {
      console.error('Failed to load news:', err);
      setError('Failed to load news from the database.');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchNews();
  }, [fetchNews]);

  const sources = useMemo(
    () => ['All', ...Array.from(new Set(items.map(i => i.source))).sort()],
    [items]
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return items.filter(item => {
      if (sourceFilter !== 'All' && item.source !== sourceFilter) return false;
      if (!term) return true;
      return (
        item.title.toLowerCase().includes(term) ||
        item.description.toLowerCase().includes(term)
      );
    });
  }, [items, sourceFilter, search]);

  // The lead-story treatment only makes sense for the true default view —
  // once the reader filters or searches, "Latest" would be misleading.
  const showFeatured = sourceFilter === 'All' && !search.trim();
  const featured = showFeatured ? filtered[0] : undefined;
  const gridItems = featured ? filtered.slice(1) : filtered;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', height: 'calc(100vh - 64px)', overflowY: 'auto', p: 4 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ sm: 'flex-end' }}
        mb={1.5}
        pb={1.5}
        sx={{ borderBottom: '3px solid', borderColor: 'error.main' }}
      >
        <Typography
          variant="h4"
          sx={{ flexGrow: 1, fontFamily: HEADLINE_FONT, fontWeight: 700, letterSpacing: 0.5 }}
        >
          OOH Industry News
        </Typography>
        <TextField
          size="small"
          label="Search news"
          value={search}
          onChange={e => setSearch(e.target.value)}
          sx={{ minWidth: 240, mb: '0 !important' }}
        />
      </Stack>

      <Stack direction="row" spacing={1} mb={3} mt={2} flexWrap="wrap" useFlexGap>
        {sources.map(source => (
          <Chip
            key={source}
            label={source}
            color={sourceFilter === source ? 'primary' : 'default'}
            onClick={() => setSourceFilter(source)}
            clickable
          />
        ))}
      </Stack>

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {filtered.length === 0 && !error && (
        <Alert severity="info">
          {items.length === 0
            ? 'No news articles yet. Articles are posted to the News collection by content agents — see docs/ARTICLE_UPDATES.md in the repository for posting instructions.'
            : 'No articles match the current filters.'}
        </Alert>
      )}

      {featured && (
        <Card
          sx={{
            mb: 4,
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            transition: 'box-shadow 0.2s, transform 0.2s',
            '&:hover': { boxShadow: 8, transform: 'translateY(-2px)', borderColor: 'error.main' },
          }}
        >
          <CardActionArea
            component="a"
            href={featured.link}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'stretch' }}
          >
            <Box sx={{ width: { xs: '100%', sm: 380 }, flexShrink: 0 }}>
              {featured.imageUrl ? (
                <CardMedia
                  component="img"
                  image={featured.imageUrl}
                  alt=""
                  sx={{ height: { xs: 200, sm: '100%' }, width: '100%', objectFit: 'cover' }}
                  onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              ) : (
                <ImagePlaceholder sx={{ height: { xs: 200, sm: '100%' } }} />
              )}
            </Box>
            <CardContent sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column' }}>
              <Stack direction="row" spacing={1} alignItems="center" mb={1.5} flexWrap="wrap" useFlexGap>
                <Chip label="Latest" size="small" sx={{ bgcolor: 'error.main', color: '#fff', fontWeight: 700 }} />
                <Chip label={featured.source} size="small" color="primary" />
                <Tooltip title={dayjs(featured.publishedAt).format('MMM D, YYYY h:mm A')}>
                  <Typography variant="caption" color="text.secondary">
                    {formatPublished(featured.publishedAt)}
                  </Typography>
                </Tooltip>
              </Stack>
              <Typography variant="h5" gutterBottom sx={{ fontFamily: HEADLINE_FONT, fontWeight: 700, lineHeight: 1.3 }}>
                {featured.title}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ flexGrow: 1 }}>
                {featured.description}
              </Typography>
              <Link
                component="span"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 2, fontSize: '0.9rem', fontWeight: 600 }}
              >
                Read article <OpenInNewIcon sx={{ fontSize: '1rem' }} />
              </Link>
            </CardContent>
          </CardActionArea>
        </Card>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr', lg: '1fr 1fr 1fr' },
          gap: 3,
        }}
      >
        {gridItems.map(item => (
          <Card
            key={item.id}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              border: '1px solid',
              borderColor: 'divider',
              transition: 'box-shadow 0.2s, transform 0.2s, border-color 0.2s',
              '&:hover': { boxShadow: 8, transform: 'translateY(-2px)', borderColor: 'error.main' },
            }}
          >
            <CardActionArea
              component="a"
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
            >
              {item.imageUrl ? (
                <CardMedia
                  component="img"
                  height="170"
                  image={item.imageUrl}
                  alt=""
                  sx={{ objectFit: 'cover' }}
                  onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              ) : (
                <ImagePlaceholder sx={{ height: 170 }} />
              )}
              <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                  <Chip label={item.source} size="small" color="primary" />
                  <Tooltip title={dayjs(item.publishedAt).format('MMM D, YYYY h:mm A')}>
                    <Typography variant="caption" color="text.secondary">
                      {formatPublished(item.publishedAt)}
                    </Typography>
                  </Tooltip>
                </Stack>
                <Typography variant="h6" gutterBottom sx={{ fontFamily: HEADLINE_FONT, fontSize: '1.1rem', lineHeight: 1.35, fontWeight: 700 }}>
                  {item.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                  {item.description}
                </Typography>
                <Link
                  component="span"
                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, mt: 1.5, fontSize: '0.85rem', fontWeight: 600 }}
                >
                  Read article <OpenInNewIcon sx={{ fontSize: '0.9rem' }} />
                </Link>
              </CardContent>
            </CardActionArea>
          </Card>
        ))}
      </Box>
    </Box>
  );
};

export default News;
